/* ==========================================================================
   PF HOTSPOT WIDGET — JS
   Powers the shoppable image hotspots used in styling blog posts:
   click a pin on a photo -> product modal with color/size selection,
   star rating, tap-to-zoom image, and Add to Cart.

   Upload as a theme asset (e.g. assets/pf-hotspot-widget.js) and include it
   once, either in the blog post template or via theme.liquid, alongside
   pf-hotspot-widget.css. Each blog post then only needs:
     1. The shared modal + lightbox markup (once per page, see usage example)
     2. Per-look hotspot config JSON blocks (see usage example)

   Dependencies assumed already present on the page:
     - Shopify's native /products/{handle}.js and /cart/add.js endpoints
     - Judge.me's widget script (already installed as an app on the theme)
     - GA4 via Google tag or GTM, reading from window.dataLayer
   ========================================================================== */

/* ==========================================================================
   ANALYTICS
   ========================================================================== */
window.dataLayer = window.dataLayer || [];

function pfTrackEvent(eventName, params) {
  window.dataLayer.push({ event: eventName, ...params });
}

// Builds a GA4 ecommerce "item" object from a product + selected variant
function pfBuildEcommerceItem(product, variant, handle) {
  return {
    item_id: handle,
    item_name: product.title,
    item_variant: variant ? variant.title : undefined,
    price: variant ? variant.price / 100 : undefined,
    quantity: 1
  };
}

/* ==========================================================================
   WIDGET LOGIC
   ========================================================================== */
(function() {
  // ---- Judge.me public widget API config ----
  // This store's Judge.me plan doesn't support "platform-independent widgets"
  // (which would let the badge div + jdgm's own script populate itself, even
  // for content added to the page after load). Instead we fetch the fully
  // rendered, sanitized star-rating badge HTML directly from Judge.me's public
  // widget API and inject it ourselves — this works on any plan since it's
  // just a plain public GET request, not the platform-independent feature.
  //
  // Fill these in from: Judge.me admin > Settings > Integrations > View API
  // tokens. Use the PUBLIC token here (never the private one) — it's meant
  // for exactly this kind of public, client-side use.
  const PF_JDGM_SHOP_DOMAIN = 'YOUR-STORE.myshopify.com'; // e.g. alex-abby.myshopify.com
  const PF_JDGM_PUBLIC_TOKEN = 'YOUR_PUBLIC_API_TOKEN';

  const allHotspotPins = []; // { pin, wrapper, handle, color, lookId } — used by the retargeting deep-link handler below

  document.querySelectorAll('.pf-look-media').forEach(mediaBlock => {
    const img = mediaBlock.querySelector('img');
    const configEl = mediaBlock.querySelector('script.pf-hotspot-config');
    if (!img || !configEl) return;

    const lookEl = mediaBlock.closest('.pf-look');
    const lookId = lookEl ? lookEl.id : null;
    const lookTitle = lookEl && lookEl.querySelector('h2') ? lookEl.querySelector('h2').textContent : null;

    let hotspots;
    try {
      hotspots = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Invalid pf-hotspot-config JSON', e);
      return;
    }

    // Wrap just the <img> (not the caption below it) so hotspots stay
    // pinned to the photo regardless of caption height.
    const wrapper = document.createElement('div');
    wrapper.className = 'pf-image-wrapper';
    img.parentNode.insertBefore(wrapper, img);
    wrapper.appendChild(img);

    hotspots.forEach(spot => {
      const pin = document.createElement('div');
      pin.className = 'pf-hotspot';
      pin.style.top = spot.top + '%';
      pin.style.left = spot.left + '%';
      pin.textContent = '+';
      pin.addEventListener('click', () => {
        pfTrackEvent('pf_hotspot_click', {
          look_id: lookId,
          look_title: lookTitle,
          product_handle: spot.handle,
          color: spot.color || null
        });
        openPfProduct(spot.handle, spot.color, lookId);
      });
      wrapper.appendChild(pin);

      allHotspotPins.push({ pin, wrapper, handle: spot.handle, color: spot.color || null, lookId });
    });
  });

  const overlay = document.getElementById('pfModalOverlay');
  const content = document.getElementById('pfModalContent');
  const lightboxOverlay = document.getElementById('pfLightboxOverlay');
  const lightboxImg = document.getElementById('pfLightboxImg');

  window.closePfModal = function() {
    overlay.classList.remove('open');
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closePfModal();
  });

  window.openPfLightbox = function(src, alt) {
    lightboxImg.src = src;
    lightboxImg.alt = alt || '';
    lightboxOverlay.classList.add('open');
  };
  window.closePfLightbox = function(e) {
    if (e) e.stopPropagation();
    lightboxOverlay.classList.remove('open');
  };
  lightboxOverlay.addEventListener('click', () => closePfLightbox());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePfLightbox();
  });

  window.openPfProduct = async function(handle, preselectColor, lookId) {
    content.innerHTML = 'Loading...';
    overlay.classList.add('open');
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      renderPfProduct(product, preselectColor, handle, lookId);
    } catch (err) {
      content.innerHTML = `<p>Sorry, couldn't load this product.</p>`;
    }
  };

  // Finds an option's position in product.options, matching case-insensitively
  // and trimming whitespace so "Color", "color", "Colour", " Color " etc. all
  // resolve the same way instead of silently failing on an exact match.
  //
  // IMPORTANT: on this store, /products/{handle}.js returns product.options
  // as an array of OBJECTS ({name, position, values}), not plain strings like
  // ["Color", "Size"]. Reading String(o) on an object gives "[object Object]",
  // which never matches — that was the actual cause of colors/sizes silently
  // combining into one dropdown. This pulls o.name when present, and falls
  // back to treating o as a plain string for stores/API versions that still
  // return options as bare name strings.
  function pfFindOptionIndex(options, candidateNames) {
    if (!Array.isArray(options)) return -1;
    return options.findIndex(o => {
      const name = (o && typeof o === 'object') ? o.name : o;
      return candidateNames.includes(String(name).trim().toLowerCase());
    });
  }

  // Maps this store's color option values to a representative hex so the
  // small circular swatch shows an actual color chip instead of a cropped
  // sliver of the full product/lifestyle photo (which is what showed before,
  // since each variant's real photo was being used as the swatch background).
  // These are best-effort approximations, not exact pantone matches — tweak
  // any of these to taste. Handles two-tone names like "Steel Grey / Rhythm
  // Orange" by splitting on "/" and rendering a split swatch. Any color name
  // not listed here falls back to the old photo-swatch behavior rather than
  // showing a blank circle.
  const PF_COLOR_HEX = {
    'black': '#111111',
    'white': '#f5f5f5',
    'cream': '#f0e6d2',
    'charcoal heather': '#4b4b4d',
    'charcoal': '#36454f',
    'heather grey': '#9a9a9a',
    'heather gray': '#9a9a9a',
    'dark grey heather': '#5a5a5c',
    'dark gray heather': '#5a5a5c',
    'light grey': '#c9c9c9',
    'light gray': '#c9c9c9',
    'steel grey': '#71797e',
    'steel gray': '#71797e',
    'grey': '#808080',
    'gray': '#808080',
    'sandstone': '#c2a878',
    'fawn': '#c9a074',
    'true blue': '#2a5ca8',
    'tahitian blue': '#1b6ca8',
    'diver blue': '#1f6f91',
    'navy': '#1b2a4a',
    'crimson': '#a8203a',
    'magenta': '#c2185b',
    'rosewood': '#65313b',
    'chive': '#6b8e4e',
    'spruce': '#2f4538',
    'dynasty green': '#14532d',
    'lime': '#a6ce39',
    'blast purple': '#6a3fa0',
    'lilac': '#c8a2c8',
    'rhythm orange': '#d8712d',
    'coral rose': '#e8817a',
    'cayenne': '#9e2a2b',
    'castlerock': '#6d6a64'
  };

  function pfColorSwatchStyle(name, image) {
    const lookup = n => PF_COLOR_HEX[String(n).trim().toLowerCase()];
    const direct = lookup(name);
    if (direct) return `background-color:${direct};`;

    // Two-tone names (e.g. "Steel Grey / Rhythm Orange") get a split swatch
    // if both halves are recognized.
    if (String(name).includes('/')) {
      const parts = String(name).split('/').map(p => lookup(p.trim())).filter(Boolean);
      if (parts.length === 2) {
        return `background: linear-gradient(135deg, ${parts[0]} 50%, ${parts[1]} 50%);`;
      }
    }

    // Unrecognized color name — fall back to the previous photo-swatch look
    // rather than rendering a blank circle.
    return `background-image:url('${image}'); background-size:cover; background-position:center;`;
  }

  function renderPfProduct(product, preselectColor, handle, lookId) {
    // Shopify's product.js returns options as an array of option names
    // (e.g. ["Color", "Size"]) and each variant has option1/option2/option3
    // corresponding to those positions.
    const colorOptionIndex = pfFindOptionIndex(product.options, ['color', 'colour']);
    const hasColors = colorOptionIndex !== -1;
    const colorKey = ['option1', 'option2', 'option3'][colorOptionIndex];

    // Derive the distinct color list (name + representative variant image)
    // directly from Shopify's variant data, since product.js doesn't return
    // a separate "colors" array the way our earlier mock data did.
    const colors = hasColors
      ? [...new Set(product.variants.map(v => v[colorKey]))].map(name => {
          const variant = product.variants.find(v => v[colorKey] === name);
          return {
            name,
            image: (variant && variant.featured_image && variant.featured_image.src) || product.featured_image
          };
        })
      : [];

    let selectedColor = hasColors
      ? (colors.find(c => c.name === preselectColor) || colors[0]).name
      : null;

    function variantsForColor(colorName) {
      return product.variants.filter(v => !hasColors || v[colorKey] === colorName);
    }

    function currentImage(colorName) {
      if (!hasColors) return product.featured_image;
      const c = colors.find(c => c.name === colorName);
      return c ? c.image : product.featured_image;
    }

    function colorAvailable(colorName) {
      return variantsForColor(colorName).some(v => v.available);
    }

    function buildSizeOptions(colorName) {
      const sizeOptionIndex = pfFindOptionIndex(product.options, ['size']);
      const sizeKey = sizeOptionIndex !== -1 ? ['option1', 'option2', 'option3'][sizeOptionIndex] : null;
      return variantsForColor(colorName).map(v =>
        `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>
          ${(sizeKey ? v[sizeKey] : v.title)}${!v.available ? ' (Sold out)' : ''} — $${(v.price / 100).toFixed(2)}
        </option>`
      ).join('');
    }

    function priceForColor(colorName) {
      const vs = variantsForColor(colorName);
      return vs.length ? vs[0].price : product.variants[0].price;
    }

    const swatchesHtml = hasColors ? `
      <span class="option-label">Color: <span id="pfSelectedColorName">${selectedColor}</span></span>
      <div class="pf-color-swatches" id="pfColorSwatches">
        ${colors.map(c => `
          <div class="pf-color-swatch ${c.name === selectedColor ? 'selected' : ''} ${!colorAvailable(c.name) ? 'unavailable' : ''}"
               data-color="${c.name}" title="${c.name}"
               style="${pfColorSwatchStyle(c.name, c.image)}"></div>
        `).join('')}
      </div>
    ` : '';

    // ---- Reviews: Judge.me preview badge ----
    // Fetched from Judge.me's public widget API (see PF_JDGM_* config above)
    // rather than left for Judge.me's own script to fill in, since that script
    // only scans the page once on load and never sees this badge, which is
    // added later when the modal opens.
    const reviewsUrl = `/products/${handle}#judgeme_product_reviews`;
    const ratingHtml = `
      <a href="${reviewsUrl}" target="_blank" rel="noopener" id="pfRatingLink" class="pf-rating-link-wrapper">
        <span id="pfRatingSlot" class="pf-no-reviews">Loading reviews…</span>
      </a>
    `;

    content.innerHTML = `
      <div class="pf-product-img-wrapper" id="pfProductImgWrapper">
        <img class="product-img" id="pfProductImg" src="${currentImage(selectedColor)}" alt="${product.title}">
        <div class="pf-zoom-hint">🔍</div>
      </div>
      <h3><a href="/products/${handle}?utm_source=blog&amp;utm_medium=styling_guide&amp;utm_campaign=${lookId || 'styling_guide'}&amp;utm_content=hotspot_product_link" target="_blank" rel="noopener" id="pfProductLink">${product.title}</a></h3>
      ${ratingHtml}
      <div class="price" id="pfPrice">$${(priceForColor(selectedColor) / 100).toFixed(2)}</div>
      ${swatchesHtml}
      <span class="option-label">Size</span>
      <select id="pfVariantSelect">${buildSizeOptions(selectedColor)}</select>
      <input type="number" id="pfQtyInput" value="1" min="1">
      <button class="atc-btn" id="pfAtcBtn">Add to Cart</button>
      <div class="status-msg" id="pfStatusMsg"></div>
    `;

    // Fetch the actual star-rating badge HTML from Judge.me's public widget
    // API and drop it into the slot rendered above. Fails safe: if the
    // config isn't filled in yet, or the request errors, the slot just
    // falls back to a plain "no reviews yet" style link instead of breaking
    // the rest of the modal.
    (async function loadPfReviewBadge() {
      const slot = document.getElementById('pfRatingSlot');
      if (!slot) return;
      if (!PF_JDGM_SHOP_DOMAIN || PF_JDGM_SHOP_DOMAIN.startsWith('YOUR-') ||
          !PF_JDGM_PUBLIC_TOKEN || PF_JDGM_PUBLIC_TOKEN.startsWith('YOUR_')) {
        slot.textContent = '';
        return;
      }
      try {
        const params = new URLSearchParams({
          shop_domain: PF_JDGM_SHOP_DOMAIN,
          external_id: product.id
        });
        const res = await fetch(`https://api.judge.me/api/v1/widgets/preview_badge?${params}`, {
          headers: { 'X-Api-Token': PF_JDGM_PUBLIC_TOKEN }
        });
        if (!res.ok) throw new Error('Judge.me widget request failed');
        const data = await res.json();
        const html = (data && (data.html || data.widget)) || '';
        if (html) {
          slot.outerHTML = html; // replace the placeholder <span> with Judge.me's own sanitized markup
        } else {
          slot.textContent = '';
        }
      } catch (err) {
        // Leave it blank rather than showing a broken "Loading…" state
        slot.textContent = '';
      }
    })();

    document.getElementById('pfRatingLink').addEventListener('click', () => {
      pfTrackEvent('pf_review_click', { look_id: lookId, product_handle: handle });
    });

    document.getElementById('pfProductLink').addEventListener('click', () => {
      pfTrackEvent('select_item', {
        look_id: lookId,
        ecommerce: { items: [pfBuildEcommerceItem(product, variantsForColor(selectedColor)[0], handle)] }
      });
    });

    document.getElementById('pfProductImgWrapper').addEventListener('click', () => {
      const img = document.getElementById('pfProductImg');
      openPfLightbox(img.src, img.alt);
      pfTrackEvent('pf_image_zoom', { product_handle: handle, look_id: lookId });
    });

    // GA4 recommended "view_item" event — fires once, when the modal first loads this product
    pfTrackEvent('view_item', {
      look_id: lookId,
      ecommerce: {
        currency: 'USD',
        value: priceForColor(selectedColor) / 100,
        items: [pfBuildEcommerceItem(product, variantsForColor(selectedColor)[0], handle)]
      }
    });

    if (hasColors) {
      document.querySelectorAll('#pfColorSwatches .pf-color-swatch').forEach(el => {
        el.addEventListener('click', () => {
          if (el.classList.contains('unavailable')) return;
          selectedColor = el.dataset.color;
          document.querySelectorAll('#pfColorSwatches .pf-color-swatch').forEach(s => s.classList.remove('selected'));
          el.classList.add('selected');
          document.getElementById('pfSelectedColorName').textContent = selectedColor;
          document.getElementById('pfProductImg').src = currentImage(selectedColor);
          document.getElementById('pfPrice').textContent = `$${(priceForColor(selectedColor) / 100).toFixed(2)}`;
          document.getElementById('pfVariantSelect').innerHTML = buildSizeOptions(selectedColor);
          pfTrackEvent('pf_color_select', { product_handle: handle, look_id: lookId, color: selectedColor });
        });
      });
    }

    document.getElementById('pfAtcBtn').addEventListener('click', async () => {
      const variantId = document.getElementById('pfVariantSelect').value;
      const qty = parseInt(document.getElementById('pfQtyInput').value, 10) || 1;
      const btn = document.getElementById('pfAtcBtn');
      const status = document.getElementById('pfStatusMsg');
      const variant = variantsForColor(selectedColor).find(v => String(v.id) === String(variantId));

      btn.disabled = true;
      btn.textContent = 'Adding...';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
        });
        if (!res.ok) throw new Error('Add to cart failed');
        status.textContent = 'Added to cart!';
        btn.textContent = 'Add to Cart';
        btn.disabled = false;

        // GA4 recommended "add_to_cart" event
        pfTrackEvent('add_to_cart', {
          look_id: lookId,
          ecommerce: {
            currency: 'USD',
            value: variant ? (variant.price / 100) * qty : undefined,
            items: [{ ...pfBuildEcommerceItem(product, variant, handle), quantity: qty }]
          }
        });

        // This theme (Out of the Sandbox "Fabric") updates its header cart
        // bubble and cart drawer by listening for a bubbling "cart:update"
        // CustomEvent on the document — its own <product-form-component>
        // dispatches this after a normal add-to-cart. Since our /cart/add.js
        // call above bypasses that component entirely, the drawer/bubble
        // never heard about it, so we dispatch the same event ourselves with
        // a freshly-fetched cart object. Confirmed live: without this, the
        // cart bubble count and drawer contents stay stale after adding from
        // this widget; with it, both update immediately.
        try {
          const cart = await (await fetch('/cart.js')).json();
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: 'cart', sourceId: 'pf-hotspot-widget', data: cart }
          }));
        } catch (cartRefreshErr) {
          // Non-fatal: the item is already in the cart either way, this only
          // affects whether the header/drawer visibly reflect it right away.
        }
      } catch (err) {
        status.textContent = 'Something went wrong. Try again.';
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
      }
    });
  }

  /* ==========================================================================
     RETARGETING DEEP-LINK HANDLER
     Reads ?highlight=<handle>&color=<color>&look=<look_id> from the URL
     (this is what a retargeting ad or email link would carry). Scrolls to
     the matching look and gives its hotspot a temporary strong pulse +
     tooltip. Deliberately does NOT auto-open the product modal — the
     styling content's job is to build context before asking for a decision,
     and auto-opening would also pollute hotspot-click analytics with clicks
     that never really happened.
     ========================================================================== */
  function pfHandleRetargetHighlight() {
    const params = new URLSearchParams(window.location.search);
    const highlightHandle = params.get('highlight');
    if (!highlightHandle) return;

    const highlightColor = params.get('color');
    const highlightLook = params.get('look');

    // Prefer an exact match on look + color, then look only, then color only, then handle only
    const candidates = allHotspotPins.filter(p => p.handle === highlightHandle);
    if (!candidates.length) return;

    const match =
      candidates.find(p => (!highlightLook || p.lookId === highlightLook) && (!highlightColor || p.color === highlightColor)) ||
      candidates.find(p => !highlightLook || p.lookId === highlightLook) ||
      candidates[0];

    // Give layout a moment to settle (images loading, fonts, etc.) before scrolling
    setTimeout(() => {
      match.wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });

      match.pin.classList.add('pf-emphasized');

      const tooltip = document.createElement('div');
      tooltip.className = 'pf-tooltip-bubble';
      tooltip.textContent = "That's the one!";
      tooltip.style.top = match.pin.style.top;
      tooltip.style.left = match.pin.style.left;
      match.wrapper.appendChild(tooltip);

      pfTrackEvent('pf_retarget_highlight_shown', {
        product_handle: highlightHandle,
        color: highlightColor || null,
        look_id: match.lookId
      });

      // Fade the tooltip after a few seconds; keep the pulse a bit longer since
      // it's more ambient/less intrusive than a persistent text bubble
      setTimeout(() => {
        tooltip.classList.add('pf-tooltip-hidden');
        setTimeout(() => tooltip.remove(), 700);
      }, 4000);

      setTimeout(() => {
        match.pin.classList.remove('pf-emphasized');
      }, 8000);
    }, 600);
  }

  pfHandleRetargetHighlight();
})();

